import { DashboardTopperProps } from "design-system/sites/alumni/dashboard-topper/dashboard-topper";
import { createContentfulProps } from "frontend/contentful/lib/create-contentful-props";
import { DashboardTopperEntry } from "frontend/contentful/schema/toppers";
import { useFetch } from "frontend/hooks/use-fetch";
import { AlumniUserProfile } from "frontend/types";
import { homeHiddenTitle } from "../utils/home-hidden-title";
import { getAlumniEndpoints } from "frontend/contentful/lib/get-endpoints";
import { usePageProps } from "frontend/hooks/use-page-props";
import { useContentful } from "frontend/hooks/use-contentful";
import { isContentType } from "frontend/contentful/lib/is-content-type";
import { AlumniContentReferencesEntry } from "frontend/contentful/schema/universal";

export const createDashboardTopperProps = createContentfulProps<
  DashboardTopperEntry,
  DashboardTopperProps
>(() => {
  const props: DashboardTopperProps = {
    title: "Welcome",
    hiddenTitle: homeHiddenTitle("Welcome"),
    items: [],
  };
  const { request, contentReferences } = usePageProps();
  const { getEntry } = useContentful();
  const siteContentReferences = contentReferences
    ? getEntry(contentReferences)
    : undefined;
  let alumniContentReferences = undefined;
  if (
    isContentType<AlumniContentReferencesEntry>(
      "alumniContentReferences",
      siteContentReferences,
    )
  ) {
    alumniContentReferences = siteContentReferences;
  }
  // Fetch Alumni data from API to populate the dashboard topper with personalized information
  const { data, error } = useFetch<AlumniUserProfile>({
    url: "/api/internal/alumni/me",
    params: {},
  });

  if (error) {
    props.title = "Sorry...";
    props.items = [
      {
        title: "Error",
        value:
          "There was an error loading the alumni data. Please try again later.",
      },
    ];
    return props;
  }

  if (!data) {
    return props;
  } else {
    const classYear = data?.currentUserDetails?.details?.classYear;
    const degree = data?.currentUserDetails?.details?.degree;
    const section = data?.currentUserDetails?.details?.section;

    props.title = "Welcome back, " + data.displayName;
    props.items = [
      {
        title: "Current Company",
        value: data.currentEmployment?.organization?.organizationName ?? "N/A",
      },
      {
        title: "Current Role",
        value: data.currentEmployment?.jobTitle ?? "N/A",
      },
      {
        title: "Location",
        value: `${data.primaryHomeAddress?.city ?? ""}${data.primaryHomeAddress?.city ? ", " : ""}${data.primaryHomeAddress?.stateCode ?? ""}${data.primaryHomeAddress?.stateCode ? ", " : ""}${data.primaryHomeAddress?.country?.description ?? ""}`,
      },
      {
        title: "Interests",
        value: (
          <>
            {data.interests
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .slice(0, 1)
              .map((interest, index) => (
                <span key={index}>{interest.desc}</span>
              ))}
            {data.interests.length > 1 && (
              <>
                {", "}
                and {data.interests.length - 1}{" "}
                {data.interests.length > 3 ? "others" : "other"}
              </>
            )}
            {data.interests.length === 0 && <span>N/A</span>}
          </>
        ),
      },
      {
        title: "Clubs",
        value: (
          <>
            {data.clubs.slice(0, 1).map((club, index) => (
              <span key={index}>{club.club.clubDescription}</span>
            ))}
            {data.clubs.length > 1 && (
              <>
                {", "}
                and {data.clubs.length - 1}{" "}
                {data.clubs.length > 3 ? "others" : "other"}
              </>
            )}
            {data.clubs.length === 0 && <span>N/A</span>}
          </>
        ),
      },
      {
        title: "HBS Affiliation",
        value: `${degree} ${classYear}${section ? ", Section " + section : ""}`,
      },
    ];

    const alumniProfileEditor = getAlumniEndpoints(
      "profileEditor",
      request.host,
      alumniContentReferences,
    );

    const alumniEmailClass = getAlumniEndpoints(
      "emailMyClass",
      request.host,
      alumniContentReferences,
    );

    props.info = {
      percentage: data.profilePercentComplete,
      href: alumniProfileEditor,
    };

    //3 buttons
    props.ctas = [
      {
        href: "/directory/my-profile",
        children: "View My Profile",
        type: "link",
      },
      {
        href: alumniProfileEditor,
        children: "Update My Profile",
        type: "primary-button",
      },
      {
        href: alumniEmailClass,
        children: "Email My Class or Section",
        type: "secondary-button",
        disabled: !data.canSendReceiveClassEmail,
      },
    ];
  }

  return props;
});
